import React, { useState, useEffect, useRef, useCallback } from "react";
import { db, auth } from "../firebase";
import { collection, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, getDocs, query, where } from "firebase/firestore";

const NUM_SLOTS        = 9;
const LOAN_MINUTES     = 30;
const PICKUP_TIMEOUT_S = 120;

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff",
    shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b",
    shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

function getSlotNumber(slot) {
  if (slot.docId && slot.docId.startsWith("slot_")) {
    const n = parseInt(slot.docId.replace("slot_", ""), 10);
    if (!isNaN(n)) return n;
  }
  if (slot.id !== undefined && slot.id !== null) return slot.id;
  return "?";
}

function formatCountdown(seconds) {
  if (seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── Station Canvas ─────────────────────────────────────────
function StationCanvas({ slots, highlightSlot }) {
  const getSlot = (num) => {
    const found = slots.find(s => {
      const n = parseInt((s.docId || "").replace("slot_", ""), 10) || s.id;
      return n === num;
    });
    return { present: found?.present ?? true, authorized: found?.authorized ?? false };
  };

  const labelColor = (num) => {
    if (highlightSlot === num) return { fill: "rgba(37,99,235,0.22)", stroke: "#2563eb", text: "#2563eb", dot: "#2563eb" };
    const s = getSlot(num);
    if (!s.present) return { fill: "rgba(239,68,68,0.18)", stroke: "#ef4444", text: "#ef4444", dot: "#ef4444" };
    return { fill: "rgba(34,197,94,0.18)", stroke: "#22c55e", text: "#22c55e", dot: "#22c55e" };
  };

  // [num, x, y] — label positions matching each powerbank
  const labels = [
    [1, 880, 490], [2, 510, 490], [3, 140, 490],
    [4, 850, 340], [5, 510, 340], [6, 170, 340],
    [7, 820, 220], [8, 510, 220], [9, 200, 220],
  ];

  return (
    <svg width="100%" viewBox="0 0 1200 1000" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="purpleBase" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#8B5CF6"/>
      <stop offset="100%" stopColor="#5B2BBF"/>
    </linearGradient>
    <linearGradient id="blackBank" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#2A2A2A"/>
      <stop offset="100%" stopColor="#050505"/>
    </linearGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="18" stdDeviation="18" floodColor="#000" floodOpacity="0.22"/>
    </filter>

    {/* ✅ symbol instead of g */}
    <symbol id="powerbank" viewBox="0 0 210 390">
    <rect x="0" y="0" width="210" height="390" rx="38" fill="#1a1a1a"/>
    <rect x="14" y="18" width="182" height="350" rx="28" fill="none" stroke="#333" strokeWidth="5"/>
    <rect x="55" y="10" width="36" height="4" rx="2" fill="#111"/>
    <rect x="120" y="10" width="36" height="4" rx="2" fill="#111"/>
  </symbol>
  </defs>

  <rect width="1200" height="1000" fill="transparent"/>
  <rect x="115" y="610" width="970" height="170" rx="14" fill="url(#purpleBase)" filter="url(#shadow)"/>
  <rect x="160" y="555" width="880" height="120" rx="12" fill="#7C3AED"/>
  <g fill="#10051f" opacity="0.65">
    <rect x="185" y="530" width="210" height="70" rx="8"/>
    <rect x="495" y="530" width="210" height="70" rx="8"/>
    <rect x="805" y="530" width="210" height="70" rx="8"/>
  </g>

  {/* ✅ width + height on every use */}
  <use href="#powerbank" x="190" y="190" width="210" height="390"/>
  <use href="#powerbank" x="495" y="190" width="210" height="390"/>
  <use href="#powerbank" x="800" y="190" width="210" height="390"/>
  <use href="#powerbank" x="160" y="310" width="210" height="390"/>
  <use href="#powerbank" x="495" y="310" width="210" height="390"/>
  <use href="#powerbank" x="830" y="310" width="210" height="390"/>
  <use href="#powerbank" x="125" y="430" width="210" height="390"/>
  <use href="#powerbank" x="495" y="430" width="210" height="390"/>
  <use href="#powerbank" x="865" y="430" width="210" height="390"/>

  <rect x="90" y="710" width="1020" height="190" rx="16" fill="url(#purpleBase)"/>
  <rect x="110" y="710" width="980" height="18" fill="#9B6DFF" opacity="0.55"/>

  {labels.map(([num, lx, ly]) => {
    const c = labelColor(num);
    const isHL = highlightSlot === num;
    return (
      <g key={num}>
        <rect x={lx} y={ly} width="180" height="60" rx="8"
          fill={c.fill} stroke={c.stroke} strokeWidth={isHL ? 2.5 : 2}
          style={isHL ? { animation: "labelBlink 1s ease infinite" } : {}}
        />
        <circle cx={lx + 23} cy={ly + 20} r="6" fill={c.dot}
          style={isHL ? { animation: "labelBlink 1s ease infinite" } : {}}
        />
        <text x={lx + 95} y={ly + 45} textAnchor="middle"
          fontSize="40" fontWeight="700" fill={c.text} fontFamily="monospace"
          style={isHL ? { animation: "labelBlink 1s ease infinite" } : {}}
        >
          PB-{String(num).padStart(2, "0")}
        </text>
      </g>
    );
  })}
</svg>
  );
}
// ── Animated return arrows ────────────────────────────────
function ReturnArrows() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep(s => (s + 1) % 3), 500);
    return () => clearInterval(interval);
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, margin: "8px auto 0" }}>
      {[0, 1, 2].map(i => {
        const lit = i <= step;
        return (
          <svg key={i} width="40" height="24" viewBox="0 0 40 24" fill="none" style={{ display: "block", marginTop: i === 0 ? 0 : -6 }}>
            <path d="M4 4L20 20L36 4" stroke={lit ? "#2563eb" : "rgba(37,99,235,0.18)"} strokeWidth={lit ? "4" : "2.5"} strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke 0.15s, stroke-width 0.15s" }} />
          </svg>
        );
      })}
    </div>
  );
}

// ── Powerbank insertion animation ─────────────────────────
function PowerbankInsert({ slotDisplay }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setFrame(f => (f + 1) % 4), 500);
    return () => clearInterval(interval);
  }, []);
  const offsets   = [0, 8, 16, 16];
  const opacities = [1, 1, 1, 0.7];
  return (
    <div style={{ borderRadius: 20, display: "inline-block", width: "100%", position: "relative", overflow: "hidden" }}>
      <img src="/powerbankimage.png" alt="Powerbank" style={{ width: "100%", display: "block", borderRadius: 20, transform: `translateY(${offsets[frame]}px)`, opacity: opacities[frame], transition: frame === 0 ? "transform 0.15s ease-in, opacity 0.15s" : "transform 0.4s ease-out, opacity 0.3s" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "20%", background: "#2d4a3e", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "0 0 20px 20px" }}>
        <span style={{ color: "#ffffff", fontSize: 22, fontWeight: 800, letterSpacing: "0.08em", textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>Slot-{slotDisplay}</span>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────
export default function PowerBank() {
  const [dark, setDark]                       = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [slots, setSlots]                     = useState([]);
  const [step, setStep]                       = useState("idle");
  const [assignedSlot, setAssignedSlot]       = useState(null);
  const [secondsLeft, setSecondsLeft]         = useState(LOAN_MINUTES * 60);
  const [pickupSecondsLeft, setPickupSeconds] = useState(PICKUP_TIMEOUT_S);
  const [activeBorrow, setActiveBorrow]       = useState(null); // active log doc if user has one
  const timerRef       = useRef(null);
  const pickupTimerRef = useRef(null);
  const logDocRef      = useRef(null);

  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  // Firestore slots listener
  useEffect(() => {
    let debounceTimer = null;
    let pending = null;
    const unsub = onSnapshot(collection(db, "powerbank_slots"), snap => {
      const data = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
      data.sort((a, b) => getSlotNumber(a) - getSlotNumber(b));
      pending = data;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => setSlots(pending), 500);
    });
    return () => { unsub(); clearTimeout(debounceTimer); };
  }, []);

  // Check for active borrow on mount and when auth changes
  useEffect(() => {
    const checkActiveBorrow = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "powerbank_logs"),
        where("userId", "==", user.uid),
        where("status", "in", ["reserved", "active"])
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const logDoc = snap.docs[0];
        setActiveBorrow({ id: logDoc.id, ...logDoc.data() });
      } else {
        setActiveBorrow(null);
      }
    };
    checkActiveBorrow();
  }, [step]); // re-check when step changes (e.g. after return)

  // Countdown timer
  useEffect(() => {
    if (step === "countdown") {
      setSecondsLeft(LOAN_MINUTES * 60);
      timerRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); setStep("overdue"); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  // Pickup timeout
  useEffect(() => {
    if (step === "assigned") {
      setPickupSeconds(PICKUP_TIMEOUT_S);
      pickupTimerRef.current = setInterval(() => {
        setPickupSeconds(prev => {
          if (prev <= 1) {
            clearInterval(pickupTimerRef.current);
            if (assignedSlot) updateDoc(doc(db, "powerbank_slots", assignedSlot.docId), { authorized: false }).catch(console.error);
            if (logDocRef.current) {
              updateDoc(logDocRef.current, { returnedAt: serverTimestamp(), status: "cancelled" }).catch(console.error);
              logDocRef.current = null;
            }
            setStep("cancelled"); setAssignedSlot(null); return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(pickupTimerRef.current);
    }
    return () => clearInterval(pickupTimerRef.current);
  }, [step, assignedSlot]);

  // Watch for slot detection in waiting step
  useEffect(() => {
    if (step !== "waiting" || !assignedSlot) return;
    const slotNum = getSlotNumber(assignedSlot);
    const found = slots.find(s => getSlotNumber(s) === slotNum);
    if (found && found.present) handleSlotDetected();
  }, [step, slots, assignedSlot]);

  const allSlotsLoaded = slots.length === NUM_SLOTS;
  const availableCount = allSlotsLoaded ? slots.filter(s => s.present && !s.authorized).length : null;

  const getRecommended = () => {
    if (!allSlotsLoaded) return null;
    const available = slots.filter(s => s.present && !s.authorized);
    if (available.length === 0) return null;
    return available.reduce((a, b) => (a.secondsIn || 0) > (b.secondsIn || 0) ? a : b);
  };

  // Resume an active borrow — restore assignedSlot from log and go to returning
  const handleResumeBorrow = () => {
    if (!activeBorrow) return;
    // Find the slot object from our slots array
    const slotDoc = slots.find(s => s.docId === activeBorrow.slotDocId);
    if (slotDoc) {
      setAssignedSlot(slotDoc);
    } else {
      // Fallback: create a minimal slot object from the log
      setAssignedSlot({ docId: activeBorrow.slotDocId, id: activeBorrow.slotId });
    }
    // Restore the log doc ref so handleSlotDetected can update it
    logDocRef.current = doc(db, "powerbank_logs", activeBorrow.id);
    setStep("returning");
  };

  const handleRequest = async () => {
    const slot = getRecommended();
    if (!slot) { setStep("none"); return; }
    const user = auth.currentUser;
    try {
      if (activeBorrow) { setStep("already_borrowed"); return; }
      await updateDoc(doc(db, "powerbank_slots", slot.docId), { authorized: true });
      const logRef = await addDoc(collection(db, "powerbank_logs"), {
        slotId: getSlotNumber(slot), slotDocId: slot.docId,
        userId: user?.uid ?? "unknown",
        userName: user?.displayName ?? user?.email ?? "Inconnu",
        takenAt: serverTimestamp(), returnedAt: null, status: "reserved",
      });
      logDocRef.current = logRef;
      setAssignedSlot(slot); setStep("assigned");
    } catch (err) { console.error(err); }
  };

  const handleConfirmGood = async () => {
    if (logDocRef.current) await updateDoc(logDocRef.current, { status: "active", condition: "good" }).catch(console.error);
    setStep("countdown");
  };

  const handleConfirmDefective = async () => {
    if (logDocRef.current) await updateDoc(logDocRef.current, { status: "active", condition: "defective" }).catch(console.error);
    if (assignedSlot) await updateDoc(doc(db, "powerbank_slots", assignedSlot.docId), { defective: true }).catch(console.error);
    setStep("countdown");
  };

  const handleReturn = () => {
    clearInterval(timerRef.current);
    setStep("returning");
  };

  const handleReturnConfirm = () => setStep("waiting");

  const handleSlotDetected = async () => {
    if (logDocRef.current) {
      await updateDoc(logDocRef.current, { returnedAt: serverTimestamp(), status: "returned" }).catch(console.error);
      logDocRef.current = null;
    }
    setActiveBorrow(null);
    setStep("done");
    setTimeout(() => { setStep("idle"); setAssignedSlot(null); }, 3000);
  };

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - secondsLeft / (LOAN_MINUTES * 60));
  const isUrgent = secondsLeft < 300;
  const slotNumber = assignedSlot ? getSlotNumber(assignedSlot) : null;
  const slotDisplay = slotNumber !== null ? String(slotNumber).padStart(2, "0") : "??";
  const highlightSlot = step === "assigned" && slotNumber !== "?" ? slotNumber : null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
        @keyframes bounceDown { 0%,100%{transform:translateY(0)} 50%{transform:translateY(10px)} }
        @keyframes slideDown { 0%{opacity:0;transform:translateY(-8px)} 100%{opacity:1;transform:translateY(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes labelBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {/* ── IDLE ── */}
          {step === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ marginBottom: 16 }}>
                <StationCanvas slots={slots} highlightSlot={null} />
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Station Powerbank</div>

              {/* Active borrow banner */}
              {activeBorrow && (
                <div style={{ background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.orange, marginBottom: 4 }}>⚡ Emprunt en cours — Slot {String(activeBorrow.slotId).padStart(2,"0")}</div>
                  <div style={{ fontSize: 12, color: t.text2 }}>Vous avez une powerbank non remise. Pensez à la remettre !</div>
                </div>
              )}

              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {!allSlotsLoaded
                  ? "Chargement des slots…"
                  : availableCount > 0
                    ? `${availableCount} powerbank${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                    : "Aucune powerbank disponible"
                }
              </div>

              {/* If active borrow: show return button prominently, borrow below */}
              {activeBorrow ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={handleResumeBorrow} style={{ width: "100%", border: "none", borderRadius: 14, background: t.orange, color: "#fff", fontSize: 16, fontWeight: 700, padding: 16, cursor: "pointer" }}>
                    🔋 Remettre ma powerbank — Slot {String(activeBorrow.slotId).padStart(2,"0")}
                  </button>
                  <button disabled style={{ width: "100%", border: "none", borderRadius: 14, background: t.bg3, color: t.text3, fontSize: 14, fontWeight: 600, padding: 12, cursor: "not-allowed", opacity: 0.5 }}>
                    ⚡ Recevoir une Powerbank
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleRequest}
                  disabled={!allSlotsLoaded || availableCount === 0}
                  style={{ width: "100%", border: "none", borderRadius: 14, background: allSlotsLoaded && availableCount > 0 ? t.blue : t.bg3, color: allSlotsLoaded && availableCount > 0 ? "#fff" : t.text3, fontSize: 16, fontWeight: 700, padding: 16, cursor: allSlotsLoaded && availableCount > 0 ? "pointer" : "not-allowed", opacity: allSlotsLoaded && availableCount > 0 ? 1 : 0.6 }}
                >
                  ⚡ Recevoir une Powerbank
                </button>
              )}

              <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>Durée de prêt : {LOAN_MINUTES} minutes</div>
            </div>
          )}

          {/* ── ASSIGNED ── */}
          {step === "assigned" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ marginBottom: 16 }}>
                <StationCanvas slots={slots} highlightSlot={highlightSlot} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Rendez-vous au slot</div>
              <div style={{ width: 120, height: 120, borderRadius: "50%", background: t.blueBg, border: `3px solid ${t.blue}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", animation: "pop 0.4s ease", boxShadow: `0 0 32px ${t.blue}44` }}>
                <span style={{ fontSize: 54, fontWeight: 900, color: t.blue, lineHeight: 1 }}>{slotDisplay}</span>
              </div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 8, lineHeight: 1.6 }}>Récupérez votre powerbank dans le slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong></div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 20, padding: "4px 12px", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: t.orange, fontWeight: 600 }}>⏱ Vous avez {LOAN_MINUTES} min pour la remettre</span>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: pickupSecondsLeft < 30 ? t.redBg : t.bg2, border: `1px solid ${pickupSecondsLeft < 30 ? t.redBorder : t.border}`, borderRadius: 20, padding: "4px 12px", marginBottom: 20, animation: pickupSecondsLeft < 30 ? "pulse 1s infinite" : "none" }}>
                <span style={{ fontSize: 12, color: pickupSecondsLeft < 30 ? t.red : t.text3, fontWeight: 600 }}>Réservation annulée dans {formatCountdown(pickupSecondsLeft)}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 12 }}>Une fois récupérée, elle est en bon état ?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={handleConfirmGood} style={{ width: "100%", border: "none", borderRadius: 14, background: t.green, color: "#fff", fontSize: 14, fontWeight: 700, padding: 14, cursor: "pointer" }}>✅ J'ai récupéré ma powerbank — elle fonctionne bien</button>
                <button onClick={handleConfirmDefective} style={{ width: "100%", border: `1.5px solid ${t.redBorder}`, borderRadius: 14, background: t.redBg, color: t.red, fontSize: 14, fontWeight: 700, padding: 14, cursor: "pointer" }}>⚠️ J'ai récupéré ma powerbank — elle est défectueuse</button>
              </div>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 12 }}>Votre retour aide à maintenir la station en bon état.</div>
            </div>
          )}

          {/* ── COUNTDOWN ── */}
          {step === "countdown" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>Temps restant</div>
              <div style={{ position: "relative", width: 160, height: 160, margin: "0 auto 24px" }}>
                <svg width="160" height="160" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="80" cy="80" r={radius} fill="none" stroke={t.bg3} strokeWidth="8" />
                  <circle cx="80" cy="80" r={radius} fill="none" stroke={isUrgent ? t.red : t.blue} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: "stroke-dashoffset 1s linear, stroke 0.5s" }} />
                </svg>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: isUrgent ? t.red : t.text1, fontVariantNumeric: "tabular-nums", animation: isUrgent ? "pulse 1s infinite" : "none" }}>{formatCountdown(secondsLeft)}</div>
                  <div style={{ fontSize: 10, color: t.text3, marginTop: 2 }}>min:sec</div>
                </div>
              </div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 6 }}>Remettez la powerbank au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong></div>
              <div style={{ fontSize: 12, color: t.text3, marginBottom: 28 }}>avant la fin du décompte</div>
              <button onClick={handleReturn} style={{ width: "100%", border: "none", borderRadius: 14, background: t.blue, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>🔋 J'ai remis la powerbank</button>
            </div>
          )}

          {/* ── OVERDUE ── */}
          {step === "overdue" && (
            <div style={{ animation: "shake 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.redBg, border: `2px solid ${t.red}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.red, marginBottom: 8 }}>Temps écoulé !</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 28, lineHeight: 1.6 }}>Veuillez remettre la powerbank au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong> immédiatement.</div>
              <button onClick={handleReturn} style={{ width: "100%", border: "none", borderRadius: 14, background: t.red, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>🔋 Remettre la powerbank</button>
            </div>
          )}

          {/* ── RETURNING ── */}
          {step === "returning" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 20 }}>Remise en place</div>
              <div style={{ width: 220, margin: "0 auto 0" }}>
                <PowerbankInsert slotDisplay={slotDisplay} />
              </div>
              <ReturnArrows />
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 6, marginTop: 20 }}>Slot <span style={{ color: t.blue }}>{slotDisplay}</span> — USB vers le bas</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Appuyez jusqu'au déclic.</div>
              <button onClick={handleReturnConfirm} style={{ width: "100%", border: "none", borderRadius: 14, background: t.green, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>✅ Powerbank remise en place</button>
            </div>
          )}

          {/* ── WAITING ── */}
          {step === "waiting" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 32 }}>Détection en cours</div>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <svg width="100" height="100" style={{ animation: "spin 1.2s linear infinite" }}>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.blue} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194" />
                </svg>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🔋</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>En attente de détection…</div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>Assurez-vous que la powerbank est bien<br />insérée dans le slot <strong style={{ color: t.blue }}>{slotDisplay}</strong>.</div>
            </div>
          )}

          {/* ── DONE ── */}
          {step === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>Powerbank remise avec succès.</div>
            </div>
          )}

          {/* ── CANCELLED ── */}
          {step === "cancelled" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Réservation annulée</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Vous n'avez pas récupéré la powerbank à temps.<br />Le slot a été libéré.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.blue, color: "#fff", fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

          {/* ── ALREADY BORROWED ── */}
          {step === "already_borrowed" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.orangeBg, border: `2px solid ${t.orange}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Emprunt en cours</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Vous avez déjà une powerbank en cours d'utilisation.<br />Remettez-la en place avant d'en emprunter une nouvelle.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Retour</button>
            </div>
          )}

          {/* ── NONE ── */}
          {step === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucune powerbank disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Tous les slots sont vides pour le moment.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}